import type { RegisterPayload } from "../../lib/api";
import { DIALECT_GROUPS, isMinorByBirthYear } from "../../lib/constants";

export interface RegisterForm {
  invite_code: string;
  username: string;
  password: string;
  password2: string;
  display_name: string;
  dialect_l1: string[];
  hometown: string;
  training_level: "vocal" | "non_vocal" | "freshman" | "";
  birth_year: string;
  gender: "F" | "M" | "other" | "";
  contact: string;
  /** 红线 14.6 — signed on this page, not a later wall. */
  consent: boolean;
}

export const EMPTY_REGISTER_FORM: RegisterForm = {
  invite_code: "",
  username: "",
  password: "",
  password2: "",
  display_name: "",
  dialect_l1: [],
  hometown: "",
  training_level: "",
  birth_year: "",
  gender: "",
  contact: "",
  consent: false,
};

export type RegisterErrors = Partial<Record<keyof RegisterForm | "consent", string>>;

const USERNAME_RE = /^[a-z0-9_]{3,32}$/;
/** Widened to `string`: the form holds raw values until they are validated. */
const VALID_DIALECTS = new Set<string>(DIALECT_GROUPS.map((d) => d.id));

/** Step 1 — 邀请码与账号. */
export function validateAccountStep(form: RegisterForm): RegisterErrors {
  const e: RegisterErrors = {};
  if (form.invite_code.trim().length === 0) e.invite_code = "请填写老师给你的班级邀请码。";
  if (!USERNAME_RE.test(form.username)) {
    e.username = "用户名用 3–32 个小写字母、数字或下划线，例如 li_xiaoyu。";
  }
  if (form.password.length < 10) e.password = "密码至少 10 位，越长越安全。";
  if (form.password2 !== form.password) e.password2 = "两次输入的密码不一样。";
  if (form.display_name.trim().length === 0) e.display_name = "请填写你的真实姓名，老师用它认人。";
  return e;
}

/** Step 2 — 背景问卷. */
export function validateProfileStep(
  form: RegisterForm,
  now = new Date().getFullYear(),
): RegisterErrors {
  const e: RegisterErrors = {};
  if (form.dialect_l1.length === 0) e.dialect_l1 = "至少选一个，选不准就挑最接近的。";
  else if (form.dialect_l1.some((d) => !VALID_DIALECTS.has(d)))
    e.dialect_l1 = "有无法识别的选项，请重新选一次。";
  if (form.training_level === "") e.training_level = "请选一个最接近你情况的。";
  const year = Number(form.birth_year);
  if (!Number.isInteger(year) || year < now - 100 || year > now) {
    e.birth_year = "请选择出生年份。";
  }
  return e;
}

export function validateRegister(
  form: RegisterForm,
  now = new Date().getFullYear(),
): RegisterErrors {
  return { ...validateAccountStep(form), ...validateProfileStep(form, now) };
}

/** Build the request body; call only on a form that passed validation. */
export function toRegisterPayload(form: RegisterForm): RegisterPayload {
  const yearText = form.birth_year.trim();
  const year = Number(yearText);
  return {
    invite_code: form.invite_code.trim(),
    username: form.username.trim(),
    password: form.password,
    display_name: form.display_name.trim(),
    ...(form.dialect_l1.length > 0 ? { dialect_l1: form.dialect_l1 } : {}),
    ...(form.training_level ? { training_level: form.training_level } : {}),
    ...(yearText && Number.isInteger(year) ? { birth_year: year } : {}),
    ...(form.hometown.trim() ? { hometown: form.hometown.trim() } : {}),
    ...(form.gender ? { gender: form.gender } : {}),
    ...(form.contact.trim() ? { contact: form.contact.trim() } : {}),
    ...(form.consent
      ? { consent: { train: true, display: true, commercial: true } }
      : {}),
  };
}

/** Shown on step 2 so a minor knows a guardian signature is coming. */
export function willNeedGuardian(form: RegisterForm, now = new Date().getFullYear()): boolean {
  const year = Number(form.birth_year);
  return Number.isInteger(year) && isMinorByBirthYear(year, now);
}
